import {
  ActivityType,
  AppleHKWorkoutActivityTypeMapping,
  getMappedWorkoutActivityType,
  getTotalSecondsInWorkout,
  UserWorkoutSession,
} from 'fitbeat.gym-core';
import { NativeEventEmitter, NativeModules, Platform } from 'react-native';
import { Logger } from '../../utils';
import i18n from '../../utils/i18n';
import ConnectedWorkoutApp, {
  AuthorizationStatus,
  HREventListener,
  IPrepareHomeWorkout,
  IPrepareStudioWorkout,
  UnRegisterEventListener,
  IAuthorizationResult,
  IOnboardable,
  INotOnboardable,
} from '../connected-workout-app';
import HealthKitManager from './healthkit-wrapper';

function getAppleMappedWorkoutActivityType(activityType: ActivityType) {
  return getMappedWorkoutActivityType(
    activityType,
    AppleHKWorkoutActivityTypeMapping,
    Logger.notify,
  );
}

class HealthKit extends ConnectedWorkoutApp {
  private readonly nativeEventEmitter: NativeEventEmitter = new NativeEventEmitter(NativeModules.HealthKitManager);

  public async isOnboardable(): Promise<IOnboardable | INotOnboardable> {
    const available = await this.isAvailable();
    if (!available) {
      return {
        isOnboardable: false,
      };
    }

    const [isAppInstalledOnConnectedDevice, isConnectedDeviceAvailable] = await Promise.all([
      this.isAppInstalledOnConnectedDevice(),
      this.isConnectedDeviceAvailable(),
    ]);

    return {
      isOnboardable: true,
      isAppInstalled: true,
      isAppInstalledOnConnectedDevice,
      isConnectedDeviceAvailable,
    };
  }

  public isAvailable(): Promise<boolean> {
    if (Platform.OS !== 'ios') {
      return Promise.resolve(false);
    }
    return HealthKitManager.isAvailable();
  }

  public async initialize(): Promise<void> {
    const workoutsAuthorized = await this.authorizationStatus();
    if (workoutsAuthorized === AuthorizationStatus.authorized) {
      await HealthKitManager.startWorkoutObserver();
    }
  }

  public async debugTestIntegration(): Promise<void> {
    const message = `Hello from ReactNative at ${Date.now()}! `
      + 'This message was sent from ReactNative, via RN bridge to iPhone native, '
      + 'and then via WatchConnectivity to the watchOS app';
    await HealthKitManager.sendTestMessage(message);
  }

  public async requestAuthorization(): Promise<IAuthorizationResult> {
    try {
      await HealthKitManager.requestAuthorization();
    } catch (error: any) {
      Logger.notify(error);
      return {
        success: false,
        errorDisplayMessage: i18n.t('heartRateDevice:unableToOpenAppleHealthPermission'),
      };
    }
    return { success: true };
  }

  public authorizationStatus(): Promise<AuthorizationStatus> {
    return HealthKitManager.workoutAuthorization();
  }

  public async allRequestedPermissionsGranted(): Promise<boolean> {
    const isAvailable = await this.isAvailable();
    if (!isAvailable) {
      return false;
    }

    const [workoutAuthorization, heartRateAuthorization] = await Promise.all([
      HealthKitManager.workoutAuthorization(),
      HealthKitManager.heartRateAuthorization(),
    ]);
    const heartRateUnavailableOrAuthorised = heartRateAuthorization === AuthorizationStatus.serviceUnavailable
      || heartRateAuthorization === AuthorizationStatus.authorized;

    return workoutAuthorization === AuthorizationStatus.authorized && heartRateUnavailableOrAuthorised;
  }

  public async stopAndSaveWorkout(
    workoutSession: UserWorkoutSession,
  ): Promise<string | void> {
    try {
      if (await HealthKitManager.workoutAuthorization() !== AuthorizationStatus.authorized) {
        return;
      }
      const start = new Date(workoutSession.startTimeOfFirstExercise).toISOString();
      const end = new Date(workoutSession.endTime).toISOString();
      const duration = getTotalSecondsInWorkout(workoutSession);
      const mappedActivityType = getAppleMappedWorkoutActivityType(workoutSession.settings.activityType);
      const exportUID = await HealthKitManager.stopAndSaveWorkout(
        start,
        end,
        duration,
        mappedActivityType,
        workoutSession.performanceMetrics?.averageHeartRate || 0,
        workoutSession.performanceMetrics?.calories || 0,
      );
      return exportUID;
    } catch (e) {
      Logger.notify('Failed to save workout to health kit');
    }
  }

  public pauseWorkout(): Promise<void> {
    return HealthKitManager.pauseWorkout();
  }

  public resumeWorkout(): Promise<void> {
    return HealthKitManager.resumeWorkout();
  }

  public async prepareHomeWorkout(options: IPrepareHomeWorkout): Promise<void> {
    const mappedActivityType = getAppleMappedWorkoutActivityType(options.activityType);
    await HealthKitManager.prepareWorkout(
      options.userId,
      mappedActivityType,
      /* start time */null,
      /* end time */null,
      /* starting zone */ null,
      /* studio url */ null,
    );
  }

  public prepareStudioWorkout(options: IPrepareStudioWorkout): Promise<void> {
    const mappedActivityType = getAppleMappedWorkoutActivityType(options.activityType);
    return HealthKitManager.prepareWorkout(
      options.userId,
      mappedActivityType,
      options.isoStartTimeOfWarmup,
      options.isoEndTime,
      options.startingZone,
      options.hrMetricsServerBaseUrl,
    );
  }

  public startWorkout(): Promise<void> {
    return HealthKitManager.startWorkout();
  }

  public stopAndDiscardWorkout(): Promise<void> {
    return HealthKitManager.stopAndDiscardWorkout();
  }

  public registerHREventHandler(listener: HREventListener): UnRegisterEventListener {
    const nativeListener = this.nativeEventEmitter.addListener('HeartRateUpdated', listener);
    return () => {
      nativeListener.remove();
    };
  }

  public isAppInstalledOnConnectedDevice(): Promise<boolean> {
    return HealthKitManager.isWatchAppInstalled();
  }

  public isConnectedDeviceAvailable(): Promise<boolean> {
    if (Platform.OS !== 'ios') {
      return Promise.resolve(false);
    }
    return HealthKitManager.watchIsAvailable();
  }

  public async canUseAsRealtimeHeartRateMonitor(): Promise<boolean> {
    const [isAllPermissionsGranted, isWatchConnected] = await Promise.all([
      this.allRequestedPermissionsGranted(), // todo: maybe check for HR available only?
      this.isAppInstalledOnConnectedDevice(),
    ]);
    return isAllPermissionsGranted && isWatchConnected;
  }
}

export default new HealthKit('Apple Health');
